'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';

export default function APIPage() {
	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<div className="inline-block px-4 py-2 mb-6 rounded-full border border-primary/50 bg-primary/10">
							<span className="text-sm font-semibold text-primary">
								Coming Soon
							</span>
						</div>
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Developer
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Platform
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							We&apos;re building a comprehensive developer
							platform with APIs, SDKs, and tools to integrate
							TabStudio&apos;s AI video generation into your
							applications.
						</p>
					</div>
				</section>

				{/* What's Coming */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							What&apos;s Coming
						</h2>
						<div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									RESTful API
								</h3>
								<p className="text-muted-foreground">
									Simple HTTP endpoints for video generation,
									asset management, and project orchestration
									with full OpenAPI documentation.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Official SDKs
								</h3>
								<p className="text-muted-foreground">
									Native libraries for Python, TypeScript, Go,
									and Rust with full type safety and
									comprehensive examples.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Webhooks & Events
								</h3>
								<p className="text-muted-foreground">
									Real-time notifications for video generation
									completion, asset uploads, and project
									updates via webhooks.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary-dark to-secondary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Interactive Docs
								</h3>
								<p className="text-muted-foreground">
									Comprehensive documentation with interactive
									API playground, code examples, and
									step-by-step tutorials.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary-dark to-accent rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									API Keys & Auth
								</h3>
								<p className="text-muted-foreground">
									Secure authentication with API keys, OAuth
									2.0 support, and fine-grained permissions
									management.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Usage Analytics
								</h3>
								<p className="text-muted-foreground">
									Detailed analytics dashboard showing API
									usage, rate limits, costs, and performance
									metrics.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Timeline */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Launch Timeline
						</h2>
						<div className="space-y-6">
							<div className="glass-gradient p-8 rounded-2xl border border-white/20">
								<div className="flex items-start gap-6">
									<div className="flex-shrink-0">
										<div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
											<span className="text-2xl font-bold text-white">
												Q2
											</span>
										</div>
									</div>
									<div className="flex-1">
										<h3 className="text-xl font-semibold mb-2">
											Beta Access
										</h3>
										<p className="text-muted-foreground mb-4">
											Limited beta access for early
											adopters. RESTful API with core
											video generation endpoints,
											authentication, and basic
											documentation.
										</p>
										<div className="flex flex-wrap gap-2">
											<span className="px-3 py-1 text-xs bg-primary/20 text-primary rounded-full">
												API v1
											</span>
											<span className="px-3 py-1 text-xs bg-primary/20 text-primary rounded-full">
												Python SDK
											</span>
											<span className="px-3 py-1 text-xs bg-primary/20 text-primary rounded-full">
												TypeScript SDK
											</span>
										</div>
									</div>
								</div>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="flex items-start gap-6">
									<div className="flex-shrink-0">
										<div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
											<span className="text-2xl font-bold text-white">
												Q3
											</span>
										</div>
									</div>
									<div className="flex-1">
										<h3 className="text-xl font-semibold mb-2">
											Public Launch
										</h3>
										<p className="text-muted-foreground mb-4">
											Full public release with
											comprehensive documentation,
											interactive API playground,
											webhooks, and additional SDKs for Go
											and Rust.
										</p>
										<div className="flex flex-wrap gap-2">
											<span className="px-3 py-1 text-xs bg-secondary/20 text-secondary rounded-full">
												Webhooks
											</span>
											<span className="px-3 py-1 text-xs bg-secondary/20 text-secondary rounded-full">
												Go SDK
											</span>
											<span className="px-3 py-1 text-xs bg-secondary/20 text-secondary rounded-full">
												Rust SDK
											</span>
											<span className="px-3 py-1 text-xs bg-secondary/20 text-secondary rounded-full">
												API Playground
											</span>
										</div>
									</div>
								</div>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="flex items-start gap-6">
									<div className="flex-shrink-0">
										<div className="w-16 h-16 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
											<span className="text-2xl font-bold text-white">
												Q4
											</span>
										</div>
									</div>
									<div className="flex-1">
										<h3 className="text-xl font-semibold mb-2">
											Enterprise Features
										</h3>
										<p className="text-muted-foreground mb-4">
											Advanced features including custom
											model deployment, dedicated
											infrastructure, SLA guarantees, and
											enterprise support.
										</p>
										<div className="flex flex-wrap gap-2">
											<span className="px-3 py-1 text-xs bg-accent/20 text-accent rounded-full">
												Custom Models
											</span>
											<span className="px-3 py-1 text-xs bg-accent/20 text-accent rounded-full">
												OAuth 2.0
											</span>
											<span className="px-3 py-1 text-xs bg-accent/20 text-accent rounded-full">
												SLA
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Early Access CTA */}
				<section className="py-24 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Start Building Today
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									Sign up and start building with
									TabStudio&apos;s developer platform.
								</p>
								<div className="flex gap-4 justify-center flex-col sm:flex-row">
									<Link href="/register" prefetch={false}>
										<Button
											variant="default"
											size="lg"
											className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
										>
											Get Started
										</Button>
									</Link>
									<Link href="/help" prefetch={false}>
										<Button
											variant="outline"
											size="lg"
											className="glass-light glass-hover border-2 border-white/30 text-white font-semibold"
										>
											Contact Us
										</Button>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
